import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import type { FrontendTool } from '@erp/rpc';

/**
 * What the agent sees when a frontend tool finishes. Every handler's return
 * value is normalized to this shape by `useFrontendTool`, so a failure reads as
 * a failure to the model instead of arriving as an arbitrary JSON blob that
 * happens to mention an error.
 */
export type FrontendToolResult<T = unknown> =
  { ok: true; data?: T } | { ok: false; error: string };

/** A handler may answer synchronously or with a promise. */
export type MaybePromise<T> = T | Promise<T>;

/** The real client-side handler behind a tool, before normalization. */
export type FrontendToolHandler<A> = (args: A) => MaybePromise<unknown>;

function isFrontendToolResult(value: unknown): value is FrontendToolResult {
  if (typeof value !== 'object' || value === null || !('ok' in value)) {
    return false;
  }
  const candidate = value as { ok: unknown; error?: unknown };
  if (candidate.ok === true) return true;
  return candidate.ok === false && typeof candidate.error === 'string';
}

/** Serializes anything thrown — including non-Errors — into one error string. */
function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    // JSON.stringify returns undefined for symbols/functions/undefined.
    return JSON.stringify(error) ?? String(error);
  } catch {
    // Circular references and BigInt throw.
    return String(error);
  }
}

/**
 * Runs a handler and normalizes its outcome. Lives at module scope on purpose:
 * a try/catch in the hook body makes React Compiler bail out of the whole hook
 * ("Todo: Support value blocks within a try/catch statement"), and the bailout
 * is silent — the hook would just quietly stop being memoized, which is what
 * keeps its returned definition referentially stable.
 */
export async function runFrontendToolHandler<A>(
  handler: FrontendToolHandler<A>,
  args: A,
  onError?: (error: unknown) => void,
): Promise<FrontendToolResult> {
  const fail = (error: unknown): FrontendToolResult => {
    onError?.(error);
    return { ok: false, error: toErrorMessage(error) };
  };
  try {
    const value = await handler(args);
    // A returned Error is treated exactly like a thrown one — it must not reach
    // the agent as a successful result.
    if (value instanceof Error) return fail(value);
    if (isFrontendToolResult(value)) return value;
    return value === undefined || value === null
      ? { ok: true }
      : { ok: true, data: value };
  } catch (error) {
    return fail(error);
  }
}

export interface FrontendToolDefinition {
  name: string;
  description: string;
  schema: FrontendTool;
  /**
   * Runs the real client-side handler and normalizes its outcome. Never throws:
   * a thrown or returned Error becomes `{ ok: false, error }`. For awaitable
   * tools the result is POSTed back to the agent (see agent-state context); for
   * fire-and-forget tools it only drives local UI state.
   */
  execute: (args: never) => Promise<FrontendToolResult>;
  /** True when the agent should block on this tool's result (round-trip). */
  awaitable: boolean;
  /**
   * False to keep the tool out of the agent's tool list without unmounting the
   * component that owns it. Treated as enabled when omitted.
   */
  enabled?: boolean;
}

export interface FrontendToolConfig<T extends z.ZodType> {
  name: string;
  description: string;
  parameters: T;
  execute: FrontendToolHandler<z.infer<T>>;
  awaitable?: boolean;
  enabled?: boolean;
  readOnlyHint?: boolean;
  onError?: (error: unknown) => void;
}

/**
 * The wire schema the agent receives for one tool. Pure data, no closures, so
 * hooks can call it during render and attach their ref-reading `execute`
 * separately (the React Compiler's ref rule counts a ref-reading function
 * handed to a non-hook call during render as a render-time access).
 */
export function buildFrontendToolSchema(input: {
  name: string;
  description: string;
  /** The tool's parameters, already emitted as JSON Schema. */
  parameters: Record<string, unknown>;
  awaitable: boolean;
  readOnlyHint?: boolean;
}): FrontendTool {
  return {
    name: input.name,
    description: input.description,
    parameters: input.parameters,
    awaitable: input.awaitable,
    ...(input.readOnlyHint !== undefined && {
      readOnlyHint: input.readOnlyHint,
    }),
  };
}

/**
 * Builds a tool definition without React: the same normalisation
 * `useFrontendTool` applies, for callers that derive N tools from a
 * declarative spec inside ONE memo (hooks cannot be called in a loop). The
 * caller owns identity stability — `execute` here is called as given.
 */
export function buildFrontendToolDefinition<T extends z.ZodType>(
  config: FrontendToolConfig<T>,
): FrontendToolDefinition {
  const awaitable = config.awaitable ?? false;
  return {
    name: config.name,
    description: config.description,
    awaitable,
    enabled: config.enabled ?? true,
    schema: buildFrontendToolSchema({
      name: config.name,
      description: config.description,
      parameters: z.toJSONSchema(config.parameters) as Record<string, unknown>,
      awaitable,
      readOnlyHint: config.readOnlyHint,
    }),
    execute: (args: z.infer<T>) =>
      runFrontendToolHandler(config.execute, args, config.onError),
  };
}

export function useFrontendTool<T extends z.ZodType>(config: {
  name: string;
  description: string;
  parameters: T;
  /**
   * The handler. Return a `FrontendToolResult` to control exactly what the
   * agent sees, or return anything else and it is wrapped as
   * `{ ok: true, data: <value> }`. Throwing (or returning an Error) produces
   * `{ ok: false, error }`.
   */
  execute: FrontendToolHandler<z.infer<T>>;
  /**
   * When true, the backend blocks the agent until the frontend POSTs the
   * handler's return value back via submitFrontendToolResult, so the agent
   * sees the result in the same turn. Defaults to false (fire-and-forget).
   */
  awaitable?: boolean;
  /**
   * False to stop offering the tool to the agent while keeping the owning
   * component mounted — e.g. a tool that only applies once a panel is open or
   * a record is in a given state. Defaults to true.
   */
  enabled?: boolean;
  /**
   * True when calling the tool has no side effects (navigation, filtering,
   * highlighting). Surfaced to the UI as `isWrite: false` on the tool-call
   * event so read-only agent actions can be presented differently from
   * mutations. Omit when the tool writes or when it's unclear.
   */
  readOnlyHint?: boolean;
  /**
   * Called with whatever `execute` threw, before the failure is normalized into
   * a result. For local reactions the generic handling can't do — a toast,
   * reverting optimistic state, a Sentry breadcrumb.
   */
  onError?: (error: unknown) => void;
}): FrontendToolDefinition {
  // Keep the latest callbacks in refs so a changing `execute` closure (which
  // captures props/state) doesn't change the tool definition and force a
  // re-registration. Written in an effect, not during render: render must stay
  // pure because React can replay or discard it.
  const executeRef = useRef(config.execute);
  const onErrorRef = useRef(config.onError);

  useEffect(() => {
    executeRef.current = config.execute;
    onErrorRef.current = config.onError;
  });

  const { name, description, readOnlyHint } = config;
  const awaitable = config.awaitable ?? false;
  const enabled = config.enabled ?? true;

  const parametersJson = JSON.stringify(z.toJSONSchema(config.parameters));

  // This useMemo is NOT redundant with React Compiler, despite what
  // react-doctor/react-compiler-no-manual-memoization says. The compiler keys
  // the returned definition on the upstream `config.parameters` — the Zod
  // instance — because it merges `parametersJson` and this object into one
  // reactive scope. An inline `z.object({...})` is a new instance every render,
  // so compiler-only memoization churns the definition, re-runs
  // `useAgentModule`'s registration effect and loops (Sentry #7543774624).
  // Depending on the derived JSON string instead is the whole point: equal
  // content compares Object.is-equal, so inline schemas stay stable.
  // Verified both ways — module-level schemas hold identity without this memo,
  // inline ones do not. Guarded by useFrontendTool.test.ts.
  return useMemo(
    (): FrontendToolDefinition => ({
      name,
      description,
      awaitable,
      enabled,
      schema: buildFrontendToolSchema({
        name,
        description,
        parameters: JSON.parse(parametersJson) as Record<string, unknown>,
        awaitable,
        readOnlyHint,
      }),
      // Ref-reading closures, created here and only ever called when the agent
      // invokes the tool — never during render — so a changing handler never
      // changes the definition's identity. Kept as an object literal (not
      // passed through a helper) for the React Compiler's ref rule.
      execute: (args: z.infer<T>) =>
        runFrontendToolHandler(
          (latest: z.infer<T>) => executeRef.current(latest),
          args,
          (error) => onErrorRef.current?.(error),
        ),
    }),
    // `parametersJson` stands in for `config.parameters`: equal schema content
    // must yield the same definition even when the Zod instance is new.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, description, parametersJson, awaitable, enabled, readOnlyHint],
  );
}
